import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  Switch,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TablePagination,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';

import { useTableState } from '../../../../hooks/useTableState';
import { hasPemissions } from '../../../../commons/utils';
import { SinDatos } from '../../../../commons/generics/SinDatos';
import { GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../commons/generics/StyledTableComponents';
import { StatusChip } from '../../../../commons/generics/StatusChip';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';
import {
  storeGetAllPositionSchedulingRules,
  storeDeletePositionSchedulingRule,
  storeRestorePositionSchedulingRule,
} from '../../../../store/position_scheduling_rules';

const constraintLabels: Record<string, string> = {
  maxWeeklyHours: 'Horas máx. semanales',
  allowRotatingShifts: 'Turnos rotativos',
  maxConsecutiveWorkDays: 'Días máx. consecutivos',
  minRestHoursBetweenShifts: 'Horas mín. descanso',
  maxDailyHours: 'Horas máx. diarias',
  minWeeklyRestDays: 'Días mín. descanso',
};

const headCells: HeadCell[] = [
  { id: 'index', label: '#', sortable: false },
  { id: 'position.name', label: 'Cargo', sortable: true },
  { id: 'constraints', label: 'Opciones Configuradas', sortable: false },
  { id: 'status', label: 'Estado', sortable: true, align: 'center' },
  { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
];

interface Props {
  onEdit: (rule: any) => void;
  onView: (rule: any) => void;
  refreshDataTrigger?: number; // to trigger reload from parent
}

export const TableReglasProgramacionPorCargo = ({ onEdit, onView, refreshDataTrigger }: Props) => {
  const dispatch = useDispatch<any>();

  const [searchTermLocal, setSearchTermLocal] = useState('');
  const [hasPermission, setHasPermission] = useState(true);

  const tableState = useTableState('positionSchedulingRules');
  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    checked,
    handleSwitchChange,
    openElem,
    setOpenElem,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    handleResponse,
    ResponseComponents,
    getSortedAndFilteredList,
    order,
    orderBy,
    handleRequestSort,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    searchTerm,
    setSearchTerm,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    isServerSide,
    updatePaginationFromList,
  } = tableState;

  const { checkingPreload: loading } = useSelector((state: any) => state.loading);
  const { list } = useSelector((state: any) => state.positionSchedulingRules);

  useEffect(() => {
    updatePaginationFromList(list);
  }, [list, updatePaginationFromList]);

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'view',
      'modulo.programador.reglas'
    );
    if (!view) {
      setHasPermission(false);
    } else {
      setCanView(true);
    }

    const edit = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'edit',
      'modulo.programador.reglas'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'delete',
      'modulo.programador.reglas'
    );
    if (del) setCanDelete(true);

    const restore = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'restore',
      'modulo.programador.reglas'
    );
    if (restore) setCanRestore(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const reloadData = (statusVal = checked ? 0 : 1) => {
    dispatch(storeGetAllPositionSchedulingRules(pagePagination, statusVal, searchTermLocal));
  };

  useEffect(() => {
    if (canView) {
      reloadData();
    }
  }, [dispatch, canView, refreshDataTrigger]);

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setSearchTermLocal(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => storeGetAllPositionSchedulingRules(1, checked ? 0 : 1, p),
      () => reloadData(checked ? 0 : 1),
      null,
      'buscar reglas'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTermLocal) {
      handleSearchHook(
        searchTermLocal,
        dispatch,
        (p: string) => storeGetAllPositionSchedulingRules(1, status, p),
        () => reloadData(status),
        null,
        'buscar reglas'
      );
    } else {
      dispatch(storeGetAllPositionSchedulingRules(1, status, ''));
    }
  };

  const onResetSearch = () => {
    setSearchTermLocal('');
    handleResetSearchHook(() => reloadData());
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(
      storeGetAllPositionSchedulingRules(value, checked ? 0 : 1, searchTermLocal)
    );
    await handleResponse(response, 'listar reglas', false);
  };

  const handleDelete = (rule: any) => {
    setOpenElem(rule);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (rule: any) => {
    setOpenElem(rule);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDeletePositionSchedulingRule(openElem.id, searchTermLocal, pagePagination)
    );
    await handleResponse(response, 'desactivar regla', true);
    if (response?.ok) setOpenElem(null);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeRestorePositionSchedulingRule(openElem.id, searchTermLocal, pagePagination)
    );
    await handleResponse(response, 'restaurar regla', true);
    if (response?.ok) setOpenElem(null);
  };

  const filteredList = getSortedAndFilteredList(
    dataTableList || [],
    ['position.name'],
    'status',
    checked ? 0 : 1
  );

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No tienes permisos para ver este contenido.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 0 }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar por cargo..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTermLocal}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTermLocal && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card
          sx={{
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table>
              <GenericTableHead
                headCells={headCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredList.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={5} align="center">
                      <SinDatos />
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredList
                    .slice(
                      isServerSide ? 0 : page * rowsPerPage,
                      isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                    )
                    .map((rule: any, index: number) => (
                      <StyledTableRow key={rule.id} hover>
                        <StyledTableCell>
                          <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {page * rowsPerPage + index + 1}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                            {rule?.position?.name || 'Cargo Desconocido'}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell>
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {rule?.constraints && Object.keys(rule.constraints).length > 0
                              ? `${Object.keys(rule.constraints).length} opciones (${Object.entries(
                                  rule.constraints
                                )
                                  .map(
                                    ([key, val]) =>
                                      `${constraintLabels[key] || key}: ${typeof val === 'boolean' ? (val ? 'Sí' : 'No') : val}`
                                  )
                                  .join(', ')})`
                              : 'Sin opciones'}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <StatusChip status={rule.status} />
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canView && onView(rule)}
                                disabled={!canView}
                                color="info"
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            <span
                              style={{
                                cursor: !canEdit || rule.status !== 1 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => canEdit && rule.status === 1 && onEdit(rule)}
                                disabled={!canEdit || rule.status !== 1}
                                color="primary"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            {rule.status === 1 ? (
                              <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canDelete && handleDelete(rule)}
                                  disabled={!canDelete}
                                  color="error"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            ) : (
                              <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canRestore && handleRestore(rule)}
                                  disabled={!canRestore}
                                  color="success"
                                >
                                  <RestoreOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                )}
              </TableBody>
            </Table>
            {filteredList.length > 0 && isServerSide && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={itemCountPagination}
                  page={pagePagination}
                  onChange={changePaginationPage}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
            {filteredList.length > 0 && !isServerSide && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            )}
          </TableContainer>
        </Card>
      )}

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
        title="Desactivar Regla"
        description="¿Está seguro de desactivar esta regla de programación? Ya no se aplicará en el calendario."
      />

      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
        title="Restaurar Regla"
        description="¿Está seguro de restaurar esta regla de programación?"
      />
    </Box>
  );
};
